// Team-library view: teams → songs grouped by songId → versions → navigate.
// Strings localized via i18n (English default, RU parity). Pure render; navigation injected.

import type { TeamLibrary, LibraryItem } from '../library.js';
import { t } from './i18n.js';

interface LibraryViewOpts {
  teams: TeamLibrary[];
  navigate: (path: string) => void;
}

interface SongGroup { songId: string; songName: string; versions: LibraryItem[]; }

function groupBySong(items: LibraryItem[]): SongGroup[] {
  const order: string[] = [];
  const map = new Map<string, SongGroup>();
  // items already newest-first from the worker; keep that order within a group.
  for (const it of items) {
    const key = it.songId ?? `__share:${it.shareId}`; // legacy shares without songId stay separate
    let g = map.get(key);
    if (!g) { g = { songId: key, songName: it.songName ?? t('library.untitled'), versions: [] }; map.set(key, g); order.push(key); }
    g.versions.push(it);
  }
  return order.map(k => map.get(k)!);
}

function fmtDate(iso: string): string { return iso.slice(0, 10); }

export function mountLibraryView(host: HTMLElement, opts: LibraryViewOpts): void {
  host.innerHTML = '';
  const root = document.createElement('div');
  root.className = 'library-view';

  const h = document.createElement('h1');
  h.textContent = t('library.title');
  root.appendChild(h);

  const teamLink = document.createElement('button');
  teamLink.dataset.role = 'my-team';
  teamLink.className = 'lib-my-team';
  teamLink.textContent = t('library.myTeam');
  teamLink.addEventListener('click', () => opts.navigate('/dashboard'));
  root.appendChild(teamLink);

  if (opts.teams.length === 0) {
    const empty = document.createElement('p');
    empty.dataset.role = 'empty';
    empty.textContent = t('library.empty');
    root.appendChild(empty);
    host.appendChild(root);
    return;
  }

  for (const team of opts.teams) {
    const section = document.createElement('section');
    section.dataset.teamId = team.teamId;

    const th = document.createElement('h2');
    th.textContent = team.teamName;
    section.appendChild(th);

    const setlists = team.setlists ?? [];
    if (setlists.length > 0) {
      const setWrap = document.createElement('div');
      setWrap.dataset.role = 'setlists';
      const sh = document.createElement('h3');
      sh.textContent = t('library.setlistsHeading');
      setWrap.appendChild(sh);
      for (const sl of setlists) {
        const row = document.createElement('button');
        row.dataset.role = 'setlist-row';
        row.dataset.setlistId = sl.setlistId;
        row.className = 'lib-setlist';
        const n = sl.itemCount;
        row.textContent = t(n === 1 ? 'library.setlistRowOne' : 'library.setlistRowMany', { name: sl.name, n, date: sl.updatedAt.slice(0, 10) });
        row.addEventListener('click', () => opts.navigate('/setlist/' + sl.setlistId));
        setWrap.appendChild(row);
      }
      section.appendChild(setWrap);
    }

    if (team.items.length === 0) {
      const e = document.createElement('p');
      e.dataset.role = 'team-empty';
      e.textContent = t('library.nothingPublished');
      section.appendChild(e);
      root.appendChild(section);
      continue;
    }

    for (const g of groupBySong(team.items)) {
      const songEl = document.createElement('div');
      songEl.dataset.songId = g.songId;
      songEl.className = 'lib-song';

      const toggle = document.createElement('button');
      toggle.dataset.role = 'song-toggle';
      const count = g.versions.length;
      const latest = g.versions[0]!;
      toggle.textContent = count > 1
        ? t('library.songRowMulti', { name: g.songName, n: count, date: fmtDate(latest.createdAt) })
        : t('library.songRowSingle', { name: g.songName, date: fmtDate(latest.createdAt) });

      const versionsWrap = document.createElement('div');
      versionsWrap.dataset.role = 'versions';
      versionsWrap.hidden = true;

      if (count === 1) {
        // Single version → row click navigates directly.
        toggle.addEventListener('click', () => opts.navigate('/' + latest.shareId));
      } else {
        for (const v of g.versions) {
          const vrow = document.createElement('button');
          vrow.dataset.role = 'version-row';
          vrow.dataset.shareId = v.shareId;
          vrow.textContent = [fmtDate(v.createdAt), v.ownerEmail].filter(Boolean).join(' · ');
          vrow.addEventListener('click', () => opts.navigate('/' + v.shareId));
          versionsWrap.appendChild(vrow);
        }
        toggle.addEventListener('click', () => { versionsWrap.hidden = !versionsWrap.hidden; });
      }

      songEl.appendChild(toggle);
      songEl.appendChild(versionsWrap);
      section.appendChild(songEl);
    }
    root.appendChild(section);
  }

  host.appendChild(root);
}
